import './assets/css/main.css';
import './assets/css/brand.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { Fragment, useEffect, useState } from "react";
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';

import GaragePage from './views/GaragePage/GaragePage';
import NewsPage from './views/NewsPage/NewsPage';
import NewsOnePage from './views/NewsOnePage/NewsOnePage';
import ServicesPage from './views/ServicesPage/ServicesPage';
import HomePage from './views/HomePage/HomePage';
import BrandPage, { BrandHubPage } from './views/BrandPage/BrandPage';
import { VAG_BRANDS } from './seo/siteSeo';
import AdminDashboard from './views/Admin/Dashboard/Dashboard';
import AdminSite from './views/Admin/Site/Site';
import AdminAppointment from './views/Admin/Appointment/Appointment';
import AdminPosts from './views/Admin/Posts/Posts';
import AdminServices from './views/Admin/Services/Services';
import AdminEmployees from './views/Admin/Employees/Employees';
import AdminForms from './views/Admin/Forms/Forms';
import AdminGallery from './views/Admin/Gallery/Gallery';
import OnlineTerminPage from './views/OnlineTerminPage/OnlineTerminPage';
import GalleryPage from './views/GalleryPage/GalleryPage';
import ImpressumPage from './views/ImpressumPage/ImpressumPage';
import DataProtectionPage from './views/DataProtectionPage/DataProtectionPage';
import AuthLogin from './views/Auth/Login';
import AuthLogout from './views/Auth/Logout';
import useAuth from './views/Auth/Auth';
import CookieBanner from './components/CookieBanner/CookieBanner';
import { AnimatePresence, motion} from "framer-motion";
import { ParallaxProvider } from 'react-scroll-parallax';
import API from './api/api';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';

const garageApi = new API();

const LoadingPlaceholder = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    style={{ backgroundColor: '#ffffff', color: '#1b4995' }}
  >
    <CircularProgress color="inherit" />
  </Box>
);

function App() {

  const location = useLocation();

  const [configuration, setConfiguration] = useState({});
  const [configurationload, setConfigurationLoad] = useState(false);

  const products = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: 'easeOut',
      },
    },
  };

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const previous = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    document.body.scrollTop = 0;
    html.style.scrollBehavior = previous;
  }, [location.pathname]);

  useEffect(() => {
    garageApi.openEndpoints.site.getAll()
      .then((getRequest) => {
        try
        {
          // API gibt die Site-Konfiguration direkt als Objekt zurück, nicht als Array
          if(getRequest && getRequest.data && Object.keys(getRequest.data).length > 0)
          {
            const configData = { ...getRequest.data };
            
            // Parse openingHours if it's a string
            try {
              if (typeof configData.openingHours === 'string') {
                configData.openingHours = JSON.parse(configData.openingHours);
              } else if (!configData.openingHours) {
                configData.openingHours = {};
              }
              // Convert array to object format if needed
              if (Array.isArray(configData.openingHours)) {
                configData.openingHours = {};
              }
            } catch (e) {
              // openingHours might not be valid JSON, keep as is or set to empty object
              configData.openingHours = configData.openingHours || {};
            }
            
            setConfiguration(configData);
          }
          setConfigurationLoad(true);
        }
        catch(e)
        {
          // Bei Fehler trotzdem laden, damit die Seite nicht blockiert wird
          setConfigurationLoad(true);
        }
      })
      .catch(() => {
        // Bei Fehler trotzdem laden, damit die Seite nicht blockiert wird
        setConfigurationLoad(true);
      });
  }, []);

  return (
    <ParallaxProvider>
      <CookieBanner />
      <AnimatePresence exitBeforeEnter>
        <Switch location={location} key={location.pathname}>
          <PrivateRoute exact path="/admin/" component={AdminDashboard}/>
          <PrivateRoute exact path="/admin/site" component={AdminSite}/>
          <PrivateRoute exact path="/admin/posts" component={AdminPosts}/>
          <PrivateRoute exact path="/admin/services" component={AdminServices}/>
          <PrivateRoute exact path="/admin/employees" component={AdminEmployees}/>
          <PrivateRoute exact path="/admin/appointment" component={AdminAppointment}/>
          <Redirect exact from="/admin/settings" to="/admin/" />
          <PrivateRoute exact path="/admin/logout" component={AuthLogout}/>
          <PrivateRoute exact path="/admin/forms" component={AdminForms}/>
          <PrivateRoute exact path="/admin/gallery" component={AdminGallery}/>

          <PublicRoute exact path="/impressum">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <ImpressumPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path="/datenschutz">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <DataProtectionPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path="/admin/login">
            <motion.section exit={{ opacity: 0 }}>
              <motion.div initial="initial" animate="animate" variants={products}>
                  <AuthLogin/>
              </motion.div>
            </motion.section>
          </PublicRoute>
          <PublicRoute exact path="/garage">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <GaragePage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path={['/vag-garage-davos', '/vag-garage-davos/']}>
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <BrandHubPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          {VAG_BRANDS.map((brand) => {
            const bare = brand.path.replace(/\/$/, '');
            return (
              <PublicRoute exact path={[bare, `${bare}/`]} key={brand.slug}>
                { configurationload ?
                  <motion.section exit={{ opacity: 0 }}>
                    <motion.div initial="initial" animate="animate" variants={products}>
                        <BrandPage configuration={configuration} slug={brand.slug}/>
                    </motion.div>
                  </motion.section>
                  :
                  <LoadingPlaceholder />
                }
              </PublicRoute>
            );
          })}
          <PublicRoute exact path="/nachrichten">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <NewsPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute path="/nachrichten/:id">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <NewsOnePage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute path="/dienstleistungen">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <ServicesPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path="/online-termin">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <OnlineTerminPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path="/galerie">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <GalleryPage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>
          <PublicRoute exact path="/">
            { configurationload ?
              <motion.section exit={{ opacity: 0 }}>
                <motion.div initial="initial" animate="animate" variants={products}>
                    <HomePage configuration={configuration}/>
                </motion.div>
              </motion.section>
              :
              <LoadingPlaceholder />
            }
          </PublicRoute>

          <ErrorRoute path="*"/>
        </Switch>
      </AnimatePresence>
    </ParallaxProvider>
  );
}


function PrivateRoute({ ...rest })
{
  const auth = useAuth()
  return (
    <>
      {
        auth.isAuth ?
        (<Route {...rest}/>)
        :
        (<Redirect to="/admin/login" />)
      }
    </>
  )
}

function PublicRoute({ children, ...rest })
{
  return (
    <Route {...rest} render={({ location }) =>
        (children)
      }
    />
  )
}

function ErrorRoute({ children, ...rest })
{
  const auth = useAuth()
  return (
    <Route {...rest} render={({ location }) =>
        auth.isAuth ?
        (<Redirect to="/admin/" />)
        :
        (<Redirect to="/" />)
      }
    />
  )
}

export default App;
